<script>
import storageService from "@/services/storage.service";
import EasyDataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";
import axios from "axios";
import serverTableMixin from "@/mixins/serverTable";
import AppIcon from "@/components/shared/AppIcon.vue";
import StatusChip from "@/components/shared/StatusChip.vue";
import TableFooter from "@/components/shared/TableFooter.vue";
import { formatDate } from "@/helpers/dateFormatter";
import { applicantNameOf, applicationNoOf } from "@/helpers/applicationRow";

// Server-side version of StatusListPage: search, sort and paging are done by the API
// and kept in the URL (?page=2&per_page=20&search=abc&sort=date&order=desc).
export default {
  name: "ServerStatusListPage",
  mixins: [serverTableMixin],
  components: { EasyDataTable, AppIcon, StatusChip, TableFooter },
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: "" },
    cardTitle: { type: String, default: "" },
    icon: { type: String, default: "file" },
    tone: { type: String, default: "" },
    byLabel: { type: String, default: "Reviewed By" },
    searchPlaceholder: { type: String, default: "Search..." },
    emptyMessage: { type: String, default: "No applications found" },
    endpoint: { type: String, required: true }, // e.g. "/applications/approved_applications/"
    status: { type: String, required: true }, // status key shown in the chip
    // true when the endpoint is the general list (all_application_details): the status
    // is then sent as a ?status= filter. Queue endpoints already return one status.
    serverStatusFilter: { type: Boolean, default: false },
  },
  data() {
    return {
      searchValueTable: "",
      selectFilterSort: null,
      // table column -> API ordering field
      sortFieldMap: { number: "application_no", name: "name", date: "created_on" },
      arrItem: [],
    };
  },
  computed: {
    headers() {
      return [
        { text: "Application No.", value: "number", sortable: true },
        { text: "Applicant Name", value: "name", sortable: true },
        { text: this.byLabel, value: "by" },
        { text: "Date", value: "date", sortable: true },
        { text: "Status", value: "status" },
      ];
    },
    // this page's own status is fixed, so it is never written to the URL
    defaultStatus() {
      return this.serverStatusFilter ? this.status : null;
    },
    dashboardPath() {
      const role = storageService.getItem("userType");
      return role === "admin" || role === "super_user"
        ? "/admin_dashboard"
        : "/staff_dashboard";
    },
  },
  created() {
    // the mixin has already read page / search / sort from the URL
    this.loadTable();
  },
  methods: {
    onPageChange(page) {
      this.serverOptions = { ...this.serverOptions, page };
    },
    onRowsChange(rowsPerPage) {
      this.serverOptions = { ...this.serverOptions, rowsPerPage, page: 1 };
    },
    tableParams() {
      const params = serverTableMixin.methods.tableParams.call(this);
      if (this.serverStatusFilter) params.status = this.status;
      return params;
    },
    requestTable(params) {
      return axios.get(this.endpoint, { params });
    },
    applyTable(data) {
      this.arrItem = (data.results || []).map((item) => {
        const rawDate = item.formatted_date || item.created_on || item.created_at;
        return {
          number: applicationNoOf(item),
          name: applicantNameOf(item),
          by: item.rev || "N/A",
          date: rawDate ? formatDate(rawDate) : "",
          status: this.status,
        };
      });
    },
  },
};
</script>

<template>
  <section class="slp">
    <!-- Heading (left) + search and Dashboard link (right), on one line -->
    <header class="ecu-page-head slp__head">
      <div>
        <h1>{{ title }}</h1>
        <p v-if="subtitle" class="ecu-page-head__sub">{{ subtitle }}</p>
      </div>

      <div class="slp__tools">
        <div class="ecu-field ecu-field--search">
          <AppIcon name="search" :size="18" />
          <input
            type="text"
            v-model="searchValueTable"
            @input="onSearchInput"
            :placeholder="searchPlaceholder"
            :aria-label="searchPlaceholder"
            autocomplete="off"
          />
          <button
            v-if="searchValueTable"
            type="button"
            class="slp__clear"
            aria-label="Clear search"
            @click="clearSearch"
          >
            <AppIcon name="x" :size="14" :stroke="2.4" />
          </button>
        </div>

        <router-link :to="dashboardPath" class="ecu-text-link">
          <AppIcon name="chevron-left" :size="16" :stroke="2.2" />
          Dashboard
        </router-link>
      </div>
    </header>

    <div class="ecu-card">

      <div class="ecu-table-wrap">
        <EasyDataTable
          header-text-direction="center"
          :headers="headers"
          :items="arrItem"
          v-model:server-options="serverOptions"
          :server-items-length="totalItems"
          :loading="tableLoading"
          :rows-items="[10, 20, 50, 100]"
          :empty-message="emptyMessage"
          theme-color="#f97316"
          hide-footer
          table-class-name="table-custom-style noWrapHeading"
        >
          <template #item-number="{ number }">
            <span class="ecu-strong">{{ number }}</span>
          </template>
          <template #item-date="{ date }">
            <span class="ecu-date">
              <AppIcon name="calendar" :size="16" />
              {{ date }}
            </span>
          </template>
          <template #item-status="{ status }">
            <StatusChip :status="status" />
          </template>
        </EasyDataTable>
      </div>

      <TableFooter
        :page="serverOptions.page"
        :rows-per-page="serverOptions.rowsPerPage"
        :total="totalItems"
        :loading="tableLoading"
        @update:page="onPageChange"
        @update:rowsPerPage="onRowsChange"
      />
    </div>
  </section>
</template>

<style scoped>
.slp {
  display: flex;
  flex-direction: column;
  gap: 22px;
}
.slp .ecu-page-head {
  margin-bottom: 0;
}
.slp__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}
.slp__tools {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-left: auto;
}
.slp__tools .ecu-field--search {
  position: relative;
  min-width: 260px;
}
.slp__tools .ecu-field--search input {
  padding-right: 40px;
}
.slp__clear {
  position: absolute;
  right: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #5f6579;
  cursor: pointer;
}
.slp__clear:hover {
  color: #e84d00;
}
.slp .ecu-card {
  padding: 22px 22px 18px;
}
.slp__cardhead {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}
.slp__cardhead .ecu-card__title {
  margin: 0;
}
@media (max-width: 767px) {
  .slp .ecu-card {
    padding: 16px 14px 14px;
  }
  .slp__tools {
    width: 100%;
    margin-left: 0;
    flex-wrap: wrap;
  }
  .slp__tools .ecu-field--search {
    flex: 1 1 100%;
    min-width: 0;
  }
}
</style>